/**
 * SFrame117 – Settlement Dashboard / Main Hub
 * Figma Frame 17 (node 876:5824)
 *
 * Dashboard (activity + plan summary) after onboarding is started; until then,
 * only the "secure the essentials" onboarding view. Welcome overlay on first visit.
 * Sidebar navigation handled by SettlementLayout + SettlementSidebar.
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import CoExecutorBanner from '../settlement/CoExecutorBanner';
import PlannerPreviewDashboard from '../settlement/PlannerPreviewDashboard';
import { useSettlement } from '../../contexts/SettlementContext';
import { apiService } from '../../utils/api';

const welcomeStorageKey = (estateId: string | undefined) =>
  estateId ? `eoae:s117-welcome-dismissed-${estateId}` : 'eoae:s117-welcome-dismissed';

const essentialsStorageKey = (estateId: string | undefined) =>
  estateId ? `eoae:s117-essentials-started-${estateId}` : 'eoae:s117-essentials-started';

export default function SFrame117() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { isLoading, error, loadPhaseProgress } = useSettlementSession(estateId ?? null);

  const [dashboardTab, setDashboardTab] = useState<'activity' | 'summary'>('activity');
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [essentialsStarted, setEssentialsStarted] = useState(false);

  let estate = null;
  let coExecutors: { userId: string; name: string; email: string; lastActive?: string }[] = [];
  let isPlannerPath = false;
  try {
    const ctx = useSettlement();
    estate = ctx.estate;
    coExecutors = ctx.coExecutors;
    isPlannerPath = ctx.isPlannerPath;
  } catch {
    // Not inside SettlementProvider
  }

  const plannerName = estate?.ownerName || 'your planner';

  const refreshEssentialsFromStorage = useCallback(() => {
    if (typeof window === 'undefined') return;
    setEssentialsStarted(localStorage.getItem(essentialsStorageKey(estateId)) === '1');
  }, [estateId]);

  useEffect(() => {
    refreshEssentialsFromStorage();
  }, [refreshEssentialsFromStorage]);

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    loadPhaseProgress('iad').then((rows) => {
      if (cancelled) return;
      if (rows.some((r) => r.status !== 'not-started')) {
        setEssentialsStarted(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [estateId, loadPhaseProgress]);

  /* First-visit welcome: local flag (same product pattern as EstateSelector + apiService tutorial flags). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof window === 'undefined') return;
      const key = welcomeStorageKey(estateId);
      if (localStorage.getItem(key) === '1') {
        if (!cancelled) setWelcomeOpen(false);
        return;
      }
      await apiService.getEstateSelectorWelcomeFlag();
      if (!cancelled) setWelcomeOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId]);

  const dismissWelcome = () => {
    localStorage.setItem(welcomeStorageKey(estateId), '1');
    setWelcomeOpen(false);
  };

  const handleGetStarted = () => {
    localStorage.setItem(essentialsStorageKey(estateId), '1');
    setEssentialsStarted(true);
    const base = estateId ? `/sframe-141/${estateId}` : '/sframe-141';
    navigate(base);
  };

  const onboardingSteps = [
    { id: 'dash', label: 'Dashboard orientation', active: true },
    { id: 'next', label: 'Secure the essentials', active: false },
    { id: 'after', label: 'Begin guided steps', active: false },
  ];

  const renderOnboardingBody = () => (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
      {/* Simplified step progression (mirrors sidebar intent for new users) */}
      <aside className="w-full shrink-0 lg:max-w-[200px]" aria-label="Onboarding steps">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Your path</p>
        <div className="flex flex-col gap-3">
          {onboardingSteps.map((s) => (
            <div
              key={s.id}
              className={`rounded-lg border px-3 py-2.5 text-sm font-medium leading-snug ${
                s.active
                  ? 'border-cyan-500 bg-cyan-50 text-stone-900 ring-1 ring-cyan-500/30'
                  : 'border-stone-200 bg-white text-stone-600'
              }`}
            >
              {s.label}
            </div>
          ))}
        </div>
      </aside>

      <div className="flex-1 min-w-0 text-center lg:text-left">
        <h1 className="font-display text-2xl sm:text-[28px] leading-tight text-stone-800 mb-6">
          Ok, let&apos;s secure the essentials.
        </h1>

        <p className="text-sm text-stone-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-4">
          Settling an estate is a long process, but these first steps are the <strong>most critical</strong>.
          We&apos;ve highlighted the urgent tasks that are best handled while information is fresh and time is of the
          essence.
        </p>

        <p className="text-sm text-stone-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-4">
          Your progress is saved. Everything you enter here automatically flows into your master dashboard, clearing the
          immediate hurdles so we can tackle the long-term journey together later.
        </p>

        <p className="text-sm text-stone-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
          If that all sounds good then let&apos;s go ahead.
        </p>

        <button
          type="button"
          onClick={handleGetStarted}
          className="px-6 py-2.5 rounded-md font-medium text-sm border-2 border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-white transition-all"
        >
          Get Started
        </button>
      </div>
    </div>
  );

  const renderPlanSummary = () => {
    const dc = estate?.deathCertStatus || 'none';
    const dcLabel =
      dc === 'approved' ? 'Received & approved' : dc === 'pending' ? 'Pending review' : dc === 'rejected' ? 'Needs attention' : 'Not uploaded yet';

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-stone-800 mb-2">Assets overview</h3>
          <p className="text-sm text-stone-600">
            {isPlannerPath
              ? `Planning data for ${plannerName} will surface here as you record accounts and property.`
              : 'Add key assets as you discover them so nothing is missed in settlement.'}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-stone-800 mb-2">Documents overview</h3>
          <p className="text-sm text-stone-600 mb-2">Death certificate: {dcLabel}</p>
          <p className="text-sm text-stone-500">Will, letters, and other filings can be tracked in the workflow steps.</p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-stone-800 mb-2">Next steps</h3>
          <ul className="text-sm text-stone-600 space-y-2 list-disc pl-4">
            <li>
              <Link
                to={estateId ? `/sframe-124/${estateId}` : '/sframe-124'}
                className="text-cyan-700 font-medium hover:text-cyan-800"
              >
                Obtain certified death certificates
              </Link>
            </li>
            <li>
              <Link
                to={estateId ? `/sframe-141/${estateId}` : '/sframe-141'}
                className="text-cyan-700 font-medium hover:text-cyan-800"
              >
                Open the Immediately After Death checklist
              </Link>
            </li>
            <li>Return to this dashboard to track overall progress.</li>
          </ul>
        </div>
      </div>
    );
  };

  const showDashboard = essentialsStarted;

  return (
    <div className="max-w-2xl mx-auto w-full">
      {/* Subtitle bar alignment: match settlement chrome with in-page title */}
      <div className="mb-8">
        <h1 className="font-display text-xl sm:text-2xl font-semibold text-stone-800 text-center sm:text-left">
          {estate?.ownerName ? `${estate.ownerName}'s dashboard` : 'Settlement dashboard'}
        </h1>
        <p className="text-sm text-stone-500 mt-1 text-center sm:text-left">
          {showDashboard ? 'Activity and plan summary' : 'Onboarding'}
        </p>
      </div>

      {welcomeOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="s117-welcome-title"
        >
          <div className="max-w-md w-full rounded-2xl bg-white p-6 shadow-xl border border-stone-200">
            <h2 id="s117-welcome-title" className="font-display text-xl font-semibold text-stone-800 mb-2">
              Welcome{estate?.ownerName ? ` — ${estate.ownerName}'s estate` : ''}
            </h2>
            <p className="text-sm text-stone-600 mb-6">
              You&apos;re in the right place. This dashboard is where you&apos;ll track settlement progress for{' '}
              <span className="font-medium text-stone-800">{plannerName}</span>. When you&apos;re ready, we&apos;ll walk
              you through the first critical steps.
            </p>
            <button
              type="button"
              onClick={dismissWelcome}
              className="w-full rounded-lg bg-cyan-700 px-6 py-3 text-sm font-semibold text-white hover:bg-cyan-800 transition-colors"
            >
              Get started
            </button>
          </div>
        </div>
      )}

      {coExecutors.length > 0 && showDashboard && (
        <CoExecutorBanner executors={coExecutors} className="mb-6" />
      )}

      {error && (
        <div className="mb-6 bg-orange-100 border border-orange-300 rounded-md p-4 text-orange-900 text-sm">{error}</div>
      )}

      {!showDashboard ? (
        <div className="mb-8">{renderOnboardingBody()}</div>
      ) : (
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 border-b border-stone-200 mb-6">
            <button
              type="button"
              onClick={() => setDashboardTab('activity')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
                dashboardTab === 'activity'
                  ? 'border-cyan-600 text-stone-900'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
            >
              Activity
            </button>
            <button
              type="button"
              onClick={() => setDashboardTab('summary')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
                dashboardTab === 'summary'
                  ? 'border-cyan-600 text-stone-900'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
            >
              Plan summary
            </button>
          </div>

          {dashboardTab === 'activity' &&
            (isPlannerPath && estateId ? (
              <div className="[&_.border-orange-300]:border-stone-200 [&_.border-orange-300]:bg-stone-50">
                <PlannerPreviewDashboard
                  estateId={estateId}
                  estateOwnerName={estate?.ownerName || 'Planner'}
                  isPostDeath={estate?.deathCertStatus === 'approved'}
                />
              </div>
            ) : (
              <div className="rounded-xl border border-stone-200 bg-stone-50 p-6 text-center text-sm text-stone-600">
                Your activity feed will appear here as you complete settlement steps.
              </div>
            ))}

          {dashboardTab === 'summary' && renderPlanSummary()}
        </div>
      )}

      {isLoading && <div className="mt-8 text-center text-stone-500 text-sm">Loading...</div>}
    </div>
  );
}
